import {
  Box,
  IconButton,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core'
import { RefreshOutlined } from '@material-ui/icons'
import { subscribeToProjectNotifications } from 'features/BillOfMaterials/store/asyncActions/subscribeToUpdates'
import { ConnectionStatus } from 'features/SignalR/store/SignalRStore'
import _ from 'lodash'
import React, { useEffect } from 'react'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import { RootAppState } from 'store/configureStore'

const useStyles = (connectionState: ConnectionStatus) =>
  makeStyles((theme: Theme) => {
    let backgroundColor = theme.palette.warning.light

    switch (connectionState) {
      case 'online':
        backgroundColor = theme.palette.info.light
        break
      case 'disconnected':
        backgroundColor = theme.palette.secondary.main
        break
    }

    return {
      connectionStateRoot: {
        position: 'absolute',
        bottom: 0,
        left: '50%',
        height: `${theme.spacing(10)}px`,
        minWidth: `${theme.breakpoints.values.sm}px`,
        borderRadius: '4px 4px 0px 0',
        transform: `translateX(-50%)`,
        backgroundColor: backgroundColor,
        color: theme.palette.getContrastText(backgroundColor),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: -1,
        opacity: 0,
        transition: 'all 1.25s ease',
        [theme.breakpoints.down(1024)]: {
          bottom: theme.spacing(9),
        },
      },
    }
  })

type Props = {
  expectedGroup: string
}

export const ConnectionState = (props: Props) => {
  const { status, error, groups, retryDescription, retryCount } =
    useAppSelector((state: RootAppState) => state.connection, _.isEqual)

  const showTimeout = React.useRef<NodeJS.Timer>(undefined)

  const [show, setShow] = React.useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [errorMessage, setErrorMessage] = React.useState<string>(undefined)
  const dispatch = useAppDispatch()

  useEffect(() => {
    clearTimeout(showTimeout.current)

    const inExpectedGroup = props.expectedGroup
      ? groups.includes(props.expectedGroup)
      : true

    if (!inExpectedGroup) {
      console.info('not in expected update group, waiting 15s to try again', {
        groups,
        expected: props.expectedGroup,
      })
      showTimeout.current = setTimeout(() => {
        // setErrorMessage('reconnecting')
        // setShow(true)
        dispatch(subscribeToProjectNotifications(props.expectedGroup))
      }, 15_000)
    }

    if (status === 'disconnected' && props.expectedGroup) {
      console.warn(
        `disconnected (should be in ${props.expectedGroup}). setting timeout to show message`
      )
      showTimeout.current = setTimeout(() => {
        // setErrorMessage('reconnecting')
        // setShow(true)
        dispatch(subscribeToProjectNotifications(props.expectedGroup))
      }, 30_000)
    }

    return () => clearTimeout(showTimeout.current)
  }, [dispatch, groups, props.expectedGroup, status])

  const classes = useStyles(status)()

  const shouldShowNow =
    window['showConnectionStatus'] || //for debugging
    error?.message ||
    errorMessage ||
    retryCount >= 3

  React.useEffect(() => {
    setShow(shouldShowNow)
  }, [shouldShowNow])

  const visibilityStyles = show
    ? { opacity: 1, zIndex: 100 }
    : { opacity: 0, zIndex: -1 }

  return (
    <div className={classes.connectionStateRoot} style={visibilityStyles}>
      <div>
        <Box display="flex">
          <Typography variant="subtitle1">{status}</Typography>
          <IconButton size="small" onClick={() => window.location.reload()}>
            <RefreshOutlined />
          </IconButton>
        </Box>
        <Typography variant="caption" component="p">
          {errorMessage}
        </Typography>
        <Typography variant="caption" hidden={!Boolean(retryDescription)}>
          {retryDescription}
        </Typography>
      </div>
    </div>
  )
}
